/**
 * The Lbs / Kgs / Metric Tons arithmetic, ported byte-for-byte from V1's
 * `kgconvert` + `mtconvert` (`/perl/js/common_html5.js:1630-1810`).
 *
 * WHY THIS EXISTS (#33560). Every 360 call site had independently reimplemented
 * the conversion as "derive everything through lbs, dividing by 2.2046, rounded
 * to 2 decimals". V1 does neither of those things:
 *
 *   1. It uses a DIFFERENT, DIRECT factor per direction. lbs->kgs multiplies by
 *      0.4536, which is NOT 1/2.2046 (= 0.45359...). On 45000 lbs that is
 *      20412.0 vs our 20411.87 — caught by diffing prod-parity test orders
 *      5109864 (native) and 5109879 (V1) off the same template.
 *   2. It rounds every computed value with `.toFixed(1)` — ONE decimal — before
 *      it ever reaches the decimal(10,2) column. 1234 lbs is 559.7 in V1 and
 *      559.74 if you round to 2dp, so the rounding is load-bearing too, not
 *      just presentational.
 *
 * Deriving through lbs is what makes the round-trip drift: V1 computes each
 * target from the SOURCE the user typed, with that pair's own factor, so
 * mt->kgs uses 999.9988 rather than (mt / 0.000453591830542594) / 2.2046.
 *
 * NOT the only conversion in V1 — do not "unify" this with the import path.
 * `/perl/cms/imports/orderupdate.pl:633-645` genuinely divides (kgs = lbs /
 * 2.2046, mt = kgs * 2.2046 * 0.000453591830542594) with no rounding, and
 * `order_import/field_spec.py` is a faithful port of THAT. Two V1 entry points,
 * two conversions; each 360 surface must match its own counterpart.
 *
 * Surfaces that map to THIS module (all wired to kgconvert/mtconvert in V1):
 *   - the order/template editor  (defs.pl:437 `mtconvert("3")`, lbs/kgs onBlur)
 *   - the dupe-order grid        (lib_editrecord.pl:21068-21078, `dupe_` prefix)
 *   - the quote lane form        (defs_quote.pl:265-266, 375-376)
 */

/** lbs -> kgs. NOT 1/2.2046 — V1 multiplies by this literal. */
export const V1_LB_TO_KG = 0.4536;
export const V1_LB_TO_KG_NEXA = 0.453592;
/** kgs -> lbs. */
export const V1_KG_TO_LB = 2.2046;
export const V1_KG_TO_LB_NEXA = 2.20462;
/** lbs -> metric tons. */
export const V1_LB_TO_MT = 0.000453591830542594;
/** kgs -> metric tons (mtconvert mode 2). */
export const V1_KG_TO_MT = 0.00099999881069999909181;
/** metric tons -> lbs (mtconvert mode 3). */
export const V1_MT_TO_LB = 2204.62;
/** metric tons -> kgs (mtconvert mode 3). */
export const V1_MT_TO_KG = 999.9988;
export const V1_MT_TO_KG_NEXA = 999.9988107;

/** V1 reads `order_customercode` for its Nexa branch (common_html5.js:1670). */
export const NEXA_CUSTOMERCODE = '4525';

export type WeightUnit = 'lbs' | 'kgs' | 'metrictons';

/** Every computed weight goes through V1's `.toFixed(1)`. */
export const v1Round = (v: number): string => v.toFixed(1);

export interface V1Weights {
  lbs: string;
  kgs: string;
  metrictons: string;
}

/**
 * The three weights V1 would produce from `value` typed into `source`.
 *
 * Each target is computed DIRECTLY from the source with that pair's own factor
 * — never chained through a third unit — because that is what V1 does and
 * chaining reintroduces the drift this module exists to remove. The source unit
 * is echoed back rounded the same way; callers that preserve the user's raw
 * keystrokes (the order form, the quote form) overwrite it afterwards.
 *
 * `isNexa` mirrors V1's customer-4525 branch. Note V1's mt->lbs arm ignores it:
 * both sides of that `if` multiply by 2204.62 (a copy/paste in
 * common_html5.js:1790-1793). Replicated deliberately — matching V1 exactly is
 * the point, and "fixing" it here would reintroduce a divergence.
 */
export function v1WeightCascade(
  source: WeightUnit,
  value: number,
  opts: { isNexa?: boolean } = {},
): V1Weights {
  const nexa = opts.isNexa === true;
  if (source === 'lbs') {
    return {
      lbs: v1Round(value),
      kgs: v1Round(value * (nexa ? V1_LB_TO_KG_NEXA : V1_LB_TO_KG)),
      metrictons: v1Round(value * V1_LB_TO_MT),
    };
  }
  if (source === 'kgs') {
    return {
      lbs: v1Round(value * (nexa ? V1_KG_TO_LB_NEXA : V1_KG_TO_LB)),
      kgs: v1Round(value),
      metrictons: v1Round(value * V1_KG_TO_MT),
    };
  }
  return {
    lbs: v1Round(value * V1_MT_TO_LB),
    kgs: v1Round(value * (nexa ? V1_MT_TO_KG_NEXA : V1_MT_TO_KG)),
    metrictons: v1Round(value),
  };
}
